import React, { useEffect, useMemo, useRef, useState } from 'react';
import WorkspaceItemMenu from './WorkspaceItemMenu';
import { storageImage } from '../../lib/img';
import { episodePreviewText } from '../../lib/episodeScript';

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 2)  return 'just now';
  if (m < 60) return `${m}m`;
  if (h < 24) return `${h}h`;
  if (d < 7)  return `${d}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Workspace right-panel feed — two-folder layout:
 *
 *   Episodes      (tool = 'episode' items)
 *   Short-form    (tool = 'short_form' items)
 *
 * Both folders auto-populate from the user's full library (no per-show
 * filter — the workspace is the user's whole catalog of generated
 * content, not just the active show). Items are tagged by their `tool`
 * column at save time, so existing data falls into the right folder
 * with no migration: every old row already has `tool` set.
 *
 * Folders are collapsible, default expanded. Count is shown in the
 * header. Empty folders render a soft empty state, not a full hide.
 *
 * After a successful build, parent passes `highlightId` — we sort that
 * row to the top of its folder, glow it briefly, and scroll it into
 * view so the user sees "Create" actually produced something.
 */
export default function RecentEpisodesFeed({
  episodes = [],
  shortForm = [],
  workspaces = [],             // folders to group episodes under
  show,                        // legacy prop — used for thumbnail cover-art fallback
  onSelectEpisode,
  onRemix,
  onRename,
  onPublish,
  onShare,
  onDownload,
  onManage,
  onTrash,
  onPlay,                      // (ep) => play/generate this episode's narration
  playingId,                   // id of the episode currently playing (for the thumb state)
  loadingId,                   // id of the episode whose audio is generating
  remixBusyId,
  highlightId,
}) {
  // Collapsed folder ids — everything is open by default; the user can
  // collapse individual folders. Keyed by workspace id, plus '__unsorted__'
  // and '__shortform__' for the two synthetic folders.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const isOpen = (id) => !collapsed.has(id);
  const toggleFolder = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [query, setQuery] = useState('');
  // Status filter: 'all' | 'ready' | 'building' | 'draft'
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  // Combined sort + search-filter + status-filter pipeline. Search runs
  // against the topic + result.title (lowercased substring match). Status
  // is decided client-side off the same flags FeedItem uses (pending →
  // Building, result → Ready, else Draft) so the filter chips match
  // exactly what the status badge shows on each row.
  const q = query.trim().toLowerCase();
  const statusOf = (ep) => ep.pending ? 'building' : (ep.result ? 'ready' : 'draft');
  const matchesFilters = (ep) => {
    if (statusFilter !== 'all' && statusOf(ep) !== statusFilter) return false;
    if (!q) return true;
    const hay = [ep.topic, ep.result?.title, ep.niche].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  };

  const sortedEpisodes = useMemo(
    () =>
      [...episodes]
        .filter(matchesFilters)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    // eslint-disable-next-line
    [episodes, q, statusFilter],
  );
  const sortedShortForm = useMemo(
    () =>
      [...shortForm]
        .filter(matchesFilters)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    // eslint-disable-next-line
    [shortForm, q, statusFilter],
  );

  // Group episodes by their workspace folder. Order: the default "Episodes"
  // folder first, other named folders alphabetically, then "Unsorted" last.
  // Folders with no episodes (after filtering) are dropped, except we always
  // keep at least an "Episodes" placeholder so the panel never looks broken.
  const folderGroups = useMemo(() => {
    const wsById = new Map((workspaces || []).map((w) => [w.id, w]));
    // The default "Episodes" folder is where every long-form episode lives
    // unless the creator filed it into a custom folder. Episodes with no
    // workspace_id (the common case) default here — never "Unsorted" — and
    // they merge with anything actually saved to a workspace literally named
    // "Episodes", so there's only ever one Episodes folder.
    const episodesWs = (workspaces || []).find(
      (w) => (w.name || '').trim().toLowerCase() === 'episodes'
    );
    // Walk parent_id up to the root to build the folder's breadcrumb path
    // (e.g. ['Episodes', 'Top performing']). Guarded against cycles.
    const crumbsFor = (wid) => {
      const path = [];
      let cur = wsById.get(wid);
      let hops = 0;
      while (cur && hops < 12) {
        path.unshift(cur.name || 'Folder');
        cur = cur.parent_id ? wsById.get(cur.parent_id) : null;
        hops += 1;
      }
      return path.length ? path : ['Folder'];
    };
    const groups = new Map();
    for (const ep of sortedEpisodes) {
      const wid = ep.workspace_id || null;
      const isDefault = !wid || (episodesWs && wid === episodesWs.id);
      const key = isDefault ? '__episodes__' : wid;
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          title: isDefault ? 'Episodes' : (wsById.get(wid)?.name || 'Folder'),
          crumbs: isDefault ? ['Episodes'] : crumbsFor(wid),
          items: [],
        });
      }
      groups.get(key).items.push(ep);
    }
    const arr = Array.from(groups.values());
    // Default "Episodes" folder first, then custom folders alphabetically.
    arr.sort((a, b) => {
      if (a.id === '__episodes__') return -1;
      if (b.id === '__episodes__') return 1;
      return a.title.localeCompare(b.title);
    });
    return arr;
  }, [sortedEpisodes, workspaces]);

  // Active filter count for the button badge. Empty query + 'all' status
  // = 0 active filters → no badge.
  const activeFilterCount =
    (statusFilter === 'all' ? 0 : 1) + (q ? 1 : 0);

  // Scroll the highlighted (just-built) row into view, and make sure its
  // folder isn't collapsed.
  const highlightRef = useRef(null);
  const highlightEp = sortedEpisodes.find((ep) => ep.id === highlightId);
  const highlightFolderId = highlightEp ? (highlightEp.workspace_id || '__episodes__') : null;
  const highlightInShortForm = !!sortedShortForm.find((ep) => ep.id === highlightId);
  useEffect(() => {
    if (!highlightId) return;
    setCollapsed((prev) => {
      if (!highlightFolderId && !highlightInShortForm) return prev;
      const next = new Set(prev);
      if (highlightFolderId) next.delete(highlightFolderId);
      if (highlightInShortForm) next.delete('__shortform__');
      return next;
    });
    const raf = requestAnimationFrame(() => {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [highlightId, highlightFolderId, highlightInShortForm]);

  return (
    <div className="ws-feed">
      {/* Search + filters row — mirrors Suno's workspace toolbar. */}
      <div className="ws-feed-tools">
        <div className="ws-feed-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search workshop…"
            aria-label="Search workshop"
          />
          {query && (
            <button
              type="button"
              className="ws-feed-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >×</button>
          )}
        </div>
        <div className="ws-feed-filter-wrap">
          <button
            type="button"
            className={`ws-feed-filter-btn${activeFilterCount > 0 ? ' has-active' : ''}`}
            onClick={() => setFilterMenuOpen(v => !v)}
            aria-haspopup="menu"
            aria-expanded={filterMenuOpen}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            <span>Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</span>
          </button>
          {filterMenuOpen && (
            <FilterMenu
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onClose={() => setFilterMenuOpen(false)}
              onClearAll={() => { setStatusFilter('all'); setQuery(''); }}
            />
          )}
        </div>
      </div>

      {/* One collapsible folder per workspace, so the panel reflects how the
          creator has organized their episodes (Folder → episodes). */}
      {folderGroups.length === 0 ? (
        <Folder crumbs={['Episodes']} count={0} open onToggle={() => {}}>
          <div className="ws-feed-empty">
            <div className="ws-feed-empty-sub">
              Your built episodes land here. Type a topic into the chat to start one.
            </div>
          </div>
        </Folder>
      ) : (
        folderGroups.map((group) => (
          <Folder
            key={group.id}
            crumbs={group.crumbs || [group.title]}
            count={group.items.length}
            open={isOpen(group.id)}
            onToggle={() => toggleFolder(group.id)}
          >
            {group.items.slice(0, 24).map((ep) => {
              const isNew = ep.id === highlightId;
              return (
                <FeedItem
                  key={ep.id}
                  ep={ep}
                  show={show}
                  kind="episode"
                  onSelect={onSelectEpisode}
                  onRemix={onRemix}
                  onRename={onRename}
                  onPublish={onPublish}
                  onShare={onShare}
                  onDownload={onDownload}
                  onManage={onManage}
                  onTrash={onTrash}
                  onPlay={onPlay}
                  isPlaying={playingId === ep.id}
                  isAudioLoading={loadingId === ep.id}
                  busy={remixBusyId === ep.id}
                  isNew={isNew}
                  itemRef={isNew ? highlightRef : null}
                />
              );
            })}
          </Folder>
        ))
      )}

      <Folder
        crumbs={['Short-form']}
        count={sortedShortForm.length}
        open={isOpen('__shortform__')}
        onToggle={() => toggleFolder('__shortform__')}
      >
        {sortedShortForm.length === 0 ? (
          <div className="ws-feed-empty">
            <div className="ws-feed-empty-sub">
              Short-form scripts (Reels / TikTok / Shorts) land here when you build them.
            </div>
          </div>
        ) : (
          sortedShortForm.slice(0, 24).map(ep => {
            const isNew = ep.id === highlightId;
            return (
              <FeedItem
                key={ep.id}
                ep={ep}
                show={show}
                kind="short_form"
                onSelect={onSelectEpisode}
                onRemix={onRemix}
                onRename={onRename}
                onPublish={onPublish}
                onShare={onShare}
                onDownload={onDownload}
                onManage={onManage}
                onTrash={onTrash}
                busy={remixBusyId === ep.id}
                isNew={isNew}
                itemRef={isNew ? highlightRef : null}
              />
            );
          })
        )}
      </Folder>
    </div>
  );
}

// ── Collapsible folder ───────────────────────────────────────────────────────
// `crumbs` is the folder path BELOW the Workshop root — e.g. ['Episodes'] or,
// once sub-folders exist, ['Episodes', 'Top performing']. The header renders
// it as a breadcrumb: Workshop › Episodes › Top performing.
function Folder({ crumbs = [], count, open, onToggle, children }) {
  return (
    <section className={`ws-feed-folder${open ? '' : ' is-collapsed'}`}>
      <button
        type="button"
        className="ws-feed-folder-head"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className={`ws-feed-folder-chev${open ? ' is-open' : ''}`} aria-hidden>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </span>
        <span className="ws-feed-folder-title">
          <span className="ws-feed-crumb-root">Workshop</span>
          {crumbs.map((seg, i) => (
            <React.Fragment key={i}>
              <span className="ws-feed-crumb-sep" aria-hidden>›</span>
              <span className="ws-feed-crumb-seg">{seg}</span>
            </React.Fragment>
          ))}
        </span>
        <span className="ws-feed-folder-count">{count}</span>
      </button>
      {open && <div className="ws-feed-folder-body">{children}</div>}
    </section>
  );
}

// ── Single row ───────────────────────────────────────────────────────────────
function FeedItem({
  ep, show, kind,
  onSelect, onRemix, onRename,
  onPublish, onShare, onDownload, onManage, onTrash,
  onPlay, isPlaying, isAudioLoading,
  busy, isNew, itemRef,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isPending = !!ep.pending;
  const status = isPending ? 'Building' : (ep.result ? 'Ready' : 'Draft');

  // Cover art: prefer the show's cover when present; otherwise fall
  // back to a brand-gradient tile with the first letter of the show
  // name (or "E"/"S" if there's no show context yet). The initial
  // gives every item a unique-looking thumbnail instead of a sea of
  // identical pink gradients.
  const hasCover = kind === 'episode' && !!show?.cover_art_url;
  const thumbStyle = hasCover
    ? { backgroundImage: `url(${storageImage(show.cover_art_url, { width: 200 })})` }
    : kind === 'short_form'
      ? { background: 'linear-gradient(155deg, #c6364a 0%, #0c0808 85%)' }
      : { background: `linear-gradient(155deg, ${show?.brand_color || '#c6364a'} 0%, #0c0808 85%)` };
  const initial = !hasCover
    ? (show?.name?.trim()?.[0] || (kind === 'short_form' ? 'S' : 'E')).toUpperCase()
    : null;

  // Pink "Ready" dot — solid #c6364a circle on lower-left of the
  // thumbnail, fires only when the build's done. Skip on pending and
  // skip on Draft rows (where the build never completed).
  const showReadyDot = status === 'Ready';

  // Remix is only meaningful for full-length episodes — short-form
  // scripts don't have a "spin-off" workflow yet.
  const canRemix = kind === 'episode' && !!onRemix && !!ep.result;

  return (
    <div
      ref={itemRef}
      className={
        `ws-feed-item${isNew ? ' ws-feed-item-new' : ''}${isPending ? ' ws-feed-item-pending' : ''}`
      }
    >
      <button
        className="ws-feed-item-main"
        onClick={() => !isPending && onSelect?.(ep)}
        disabled={isPending}
        aria-busy={isPending}
      >
        <div
          className={`ws-feed-thumb${showReadyDot ? ' has-ready-dot' : ''}`}
          style={thumbStyle}
        >
          {/* Cover-art initial fallback — only visible when there's
              no show cover image, hidden behind the spinner while
              pending. */}
          {initial && !isPending && (
            <span className="ws-feed-thumb-initial" aria-hidden>{initial}</span>
          )}
          {isPending && (
            <span className="ws-feed-thumb-spinner" aria-hidden>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                <path d="M21 12a9 9 0 0 1-9 9" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.9s" repeatCount="indefinite" />
                </path>
              </svg>
            </span>
          )}
          {/* Hover-play — plays the episode's narration in the bottom bar.
              A span (not a button) so it doesn't nest inside the row button;
              stops propagation so it never opens the editor. */}
          {kind === 'episode' && ep.result && !isPending && onPlay && (
            <span
              className={`ws-feed-thumb-play${isPlaying ? ' is-playing' : ''}${isAudioLoading ? ' is-loading' : ''}`}
              role="button"
              tabIndex={0}
              aria-label={isPlaying ? 'Pause narration' : 'Play episode audio'}
              title={isPlaying ? 'Pause' : 'Play episode audio'}
              onClick={(e) => { e.stopPropagation(); onPlay(ep); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onPlay(ep); } }}
            >
              {isAudioLoading ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
                  <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                  <path d="M21 12a9 9 0 0 1-9 9" strokeLinecap="round">
                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.9s" repeatCount="indefinite" />
                  </path>
                </svg>
              ) : isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <polygon points="7,4 20,12 7,20" />
                </svg>
              )}
            </span>
          )}
        </div>
        <div className="ws-feed-meta">
          <div className="ws-feed-title" title={ep.topic || (kind === 'short_form' ? 'Untitled short' : 'Untitled episode')}>{ep.topic || (kind === 'short_form' ? 'Untitled short' : 'Untitled episode')}</div>
          {(() => {
            // A short taste of the actual writing, under the title.
            const preview = episodePreviewText(ep.result);
            return preview ? <div className="ws-feed-preview">{preview}</div> : null;
          })()}
          <div className="ws-feed-sub">
            <span className={`ws-feed-status ws-feed-status-${status.toLowerCase()}`}>{status}</span>
            <span className="ws-feed-time">{isPending ? 'just now' : timeAgo(ep.created_at)}</span>
          </div>
        </div>
      </button>

      {/* Row actions — a quick visible Spin-off icon (episodes only) plus
          the ··· menu. Both stop propagation so they don't open the editor.
          Hidden on a pending build. */}
      {!isPending && (
        <div className="ws-feed-actions">
          {canRemix && (
            <button
              type="button"
              className="ws-feed-action-btn"
              onClick={(e) => { e.stopPropagation(); if (!busy) onRemix(ep); }}
              disabled={busy}
              aria-label="Spin-off"
              title="Spin-off a new episode from this"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
                <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
                <line x1="4" y1="4" x2="9" y2="9"/>
              </svg>
            </button>
          )}
          <div className="ws-feed-menu-wrap">
            <button
              type="button"
              className="ws-feed-menu-btn"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <circle cx="5"  cy="12" r="1.6"/>
                <circle cx="12" cy="12" r="1.6"/>
                <circle cx="19" cy="12" r="1.6"/>
              </svg>
            </button>
            <WorkspaceItemMenu
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              onSpinOff={canRemix ? () => onRemix(ep) : null}
              onRename={onRename ? () => onRename(ep) : null}
              onEdit={ep.result ? () => onSelect?.(ep) : null}
              onPublish={onPublish ? () => onPublish(ep) : null}
              onShare={onShare ? () => onShare(ep) : null}
              onDownload={onDownload ? () => onDownload(ep) : null}
              onManage={onManage ? () => onManage(ep) : null}
              onTrash={onTrash ? () => onTrash(ep) : null}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Filters dropdown ─────────────────────────────────────────────────────────
function FilterMenu({ statusFilter, onStatusFilterChange, onClose, onClearAll }) {
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) onClose?.(); }
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const STATUSES = [
    { id: 'all',      label: 'All' },
    { id: 'ready',    label: 'Ready' },
    { id: 'building', label: 'Building' },
    { id: 'draft',    label: 'Draft' },
  ];

  return (
    <div className="ws-feed-filter-menu" ref={ref} role="menu">
      <div className="ws-feed-filter-section">
        <div className="ws-feed-filter-label">Status</div>
        <div className="ws-feed-filter-chips">
          {STATUSES.map(s => (
            <button
              key={s.id}
              type="button"
              className={`ws-feed-filter-chip${statusFilter === s.id ? ' is-active' : ''}`}
              onClick={() => onStatusFilterChange(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="ws-feed-filter-foot">
        <button type="button" className="ws-feed-filter-clear" onClick={onClearAll}>
          Clear all
        </button>
      </div>
    </div>
  );
}
